// FCP design system — Button primitive (v2.8.23: warning/success/danger-ghost added)

import React from 'react';
import './Button.css';

/**
 * Button — primary interactive element.
 *
 * Variants:
 *   - primary       → brand color, used for the ONE main CTA per screen
 *   - secondary     → bordered surface, used for everything else
 *   - ghost         → no background, used for tertiary / inline actions
 *   - danger        → muted red, destructive actions (full prominence)
 *   - warning       → amber, caution / "are you sure?" confirms
 *   - success       → green, positive confirms (complete, approve)
 *   - danger-ghost  → transparent, danger-soft on hover. Inline delete
 *                     in lists (rows, items) where full danger would
 *                     be too loud.
 *
 * Sizes:
 *   - sm  → 32px tall, 13.5/13px text, dense lists
 *   - md  → 40px tall (default)
 *   - lg  → 48px tall, large CTAs (clock in/out, submit forms)
 *
 * Touch-friendly: even sm has 32px target; md/lg meet 44px guideline
 * with the click area extended via padding.
 */
export const Button = React.forwardRef(function Button(
  {
    variant = 'secondary',
    size = 'md',
    fullWidth = false,
    loading = false,
    disabled = false,
    leftIcon,
    rightIcon,
    type = 'button',
    children,
    className = '',
    ...rest
  },
  ref
) {
  const classes = [
    'fcp-btn',
    `fcp-btn--${variant}`,
    `fcp-btn--${size}`,
    fullWidth && 'fcp-btn--full',
    loading && 'fcp-btn--loading',
    className,
  ].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={classes}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="fcp-btn__spinner" aria-hidden="true" />}
      {!loading && leftIcon && <span className="fcp-btn__icon">{leftIcon}</span>}
      <span className="fcp-btn__label">{children}</span>
      {!loading && rightIcon && <span className="fcp-btn__icon">{rightIcon}</span>}
    </button>
  );
});

export default Button;
